// Asset ID Creator. The Field Asset ID Creator workbook on the iPad, step 1 of the program.
// Words: ASSET_IDS_SECTION_WORDS.md rev 3.2 (approved 2026-10-21, 9:02 PM), ASSET_ID_PAGE_WORDS.md (the math,
// records, rules). Mockup: MOCKUP_ASSET_ID_CREATOR.html.
// Addresses (survey-page.ts routes them):
//   #/assets/{site}           the sheet
//   #/assets/{site}/{room}    Add Asset from the Map: the blank row starts in that room ("new" when the Map has none)
//
// The only place in the app that makes an asset. One row per asset, laid out as the workbook. No Save button.
// Rev 6 (ASSET_ID_SHEET_REV6_WORDS.md, approved 2026-10-23, 5:32 AM; mockup MOCKUP_ASSET_ID_SHEET_REV6.html):
//   * A row is added only by a double-tap on a row number: a blank row right under it. Nothing else adds one.
//   * Double-tap an empty cell on the new row: it fills from the row above (copy, or step END / Equip #).
//     Double-tap the empty Asset ID: the whole row fills from above, one cell stepped and outlined red.
//   * The new row saves the moment it is complete and its Asset ID is new. Usually the last of the four answers.
//   * Nothing scrolls on its own. Open space under the last row lets the bottom rows come up by hand.
//   * No bottom bar. One thin line under the top bar shows only when something needs you. Aa sizes the sheet.
// Works with no signal: a new ID goes into the site pack on this iPad at once, so the Map can place it,
// and waits in the Outbox until the cloud takes it (survey-sync.ts). Every change also leaves one history
// record (rule 32). Rules: firestore.rules rules 31 and 32, admin covering the site.

import "./asset-id.css";
import type { SignInClaims } from "./badge";
import { cloudAsset, loadDriveSettings, packAssetOf, watchDriveRequest, watchSiteDriveFolder, type SurveySite } from "./firebase";
import { driveReadDue, masterName, type DriveSettings } from "./drive-folders";
import { driveChip, siteHasFolder } from "./drive-screens";
import { isOnline } from "./offline";
import { deviceList, type RecordEntry } from "./outbox";
import { onOutboxChange, saveDrivePdf, saveRecord } from "./survey-sync";
import type { SitePack } from "./survey";
import {
  classesOf,
  actionOf,
  assetIdOf,
  BLANK_FORM,
  changeIdOf,
  changeRecordOf,
  changesOf,
  cleanPart,
  tagText,
  TAG_MAX,
  copyDownForm,
  copyDownSeries,
  editRecord,
  fillFromAbove,
  FILL_FIELDS,
  withExposure,
  type DefinedField,
  type FillField,
  formForRoom,
  formOfRecord,
  groupAssets,
  listRoomOf,
  canRetypeId,
  ID_FIELDS,
  missingFields,
  newAssetRecord,
  padPart,
  nextFreeNumber,
  removeWarning,
  shockLabelOnly,
  siteIdentifier,
  steppedField,
  WORKBOOK_CLASSES,
  type ChangeAction,
  type ClassOption,
  type IdForm,
  type ListAsset,
} from "./asset-id";
import { bringInSheet, creatorPage, downToSheet, listValues, removeSheet, type RowStatus, type SheetRow } from "./asset-id-screens";
import { bringIn, walkRowsOf } from "./bring-in";
import { fromWalk, pushChanges, roomChanges, roomOnly } from "./sheet-lock";
import { loadSaved as loadFieldWalkHere, markRoomPushed, stampQrInWalk, unpushInWalk } from "./field-labels-page";
import { DRAFT_KEY, RUN_KEY } from "./run-store";
import { qrEntries, qrPrintKey, qrRollOf } from "./qr-roll";
import type { FieldSite } from "./field-labels-site";
import { FOOTER_LINE1, qrLabelsPdf } from "./qr-label-pdf";
import { readOffset } from "./printer-offset";
import { esc } from "./training-screens";
import { cardClick, cardFind, downloadFile, newPcState, pickedOf, printCard, sectionsOf, seedPicks, shareFile, shortDate, type PcRoll, type PcState } from "./print-card";
import { assembleMaster } from "./master-pdf";
import { loadMasterState, retryMaster, sendMaster, stopMasterWatches } from "./master-send";

export interface IdsHost {
  root: HTMLElement;
  badge: SignInClaims;
  /** Draws the shared header, then `above` full width. */
  frame: (above: string, inner: string) => void;
  siteName: string;
  /** The Map rebuilds its registry from the pack (survey-map-page.ts). */
  assetsChanged: () => void;
  /** This iPad's name, for the change history. */
  deviceName: () => string;
  /** The badge holds the IEA Site Survey switch: Back To Map can show. */
  canMap: boolean;
  refreshPack: () => void;
  packBusy: () => boolean;
}

type Sheet = { kind: "downTo"; to: string } | { kind: "remove"; id: string } | { kind: "bringIn"; room: string | null } | {
      /** The one print card, rev 5 (COHESION_CHANGE_LOG.md list 2, MOCKUP_ONE_PRINT_REV5.html). */
      kind: "print";
      pc: PcState;
    };

interface State {
  pack: SitePack;
  /** The blank row at the bottom. */
  draft: IdForm;
  /** The picked row: an Asset ID, "new" for the blank row, or null. */
  pick: string | null;
  flash: { text: string; tone: "green" | "amber" | "red" } | null;
  find: string;
  sheet: Sheet | null;
  showRemoved: boolean;
  /** Opened from the Map's Add Asset: Back To Map shows. */
  fromMap: boolean;
  /** Scroll the picked row into view on the next paint. */
  scrollTo: boolean;
  /** The new row sits right under this saved row (Copy Down). Null: at the bottom. */
  after: string | null;
  /** The cell Copy Down stepped, outlined red until you change it or move off the row. */
  check: keyof IdForm | null;
  /** Copy Down To rows still to come, after the new row. Each becomes the new row in turn. */
  queue: IdForm[];
  /** Show Calculations. Remembered on this iPad. */
  calcs: boolean;
  /** Working through a Copy Down To run: each row, the last one too, saves the moment it is complete. */
  run: boolean;
  /** The row number held: its menu is open. */
  menu: string | null;
  /** The new row exists. Only a double-tap on a row number makes one (rev 6). */
  hasDraft: boolean;
  /** The saved row right above the new row: where a double-tap fills from. */
  above: string | null;
  /** Aa: the sheet's size. Remembered on this iPad. */
  zoom: number;
  /** A list cell's list is open. */
  list: { key: string; cell: keyof IdForm; left: number; top: number } | null;
  /** A column header's definition card is open. */
  def: { field: DefinedField; left: number; top: number } | null;
}

const NEW = "new";
let host: IdsHost | null = null;
let st: State | null = null;
let wired = false;
/** A saved row's Sites Equipment Tag while it is being typed, so a repaint keeps what was typed. */
let tagTyping: { key: string; value: string } | null = null;
/** True while the page is being swapped (see paint). */
let painting = false;
/** True while a run of rows saves (Fill Down, labels): Outbox changes wait to repaint until it ends. */
let bulk = false;
let paintQueued = false;

/** Outbox changes come in bursts. Repaint once per frame, and not mid-run. */
function schedulePaint(): void {
  if (paintQueued || bulk) return;
  paintQueued = true;
  requestAnimationFrame(() => {
    paintQueued = false;
    if (onThisPage() && !bulk) paint();
  });
}
/** This iPad's asset saves at this site, sent or not. Refreshed on every Outbox change. */
let local: RecordEntry[] = [];
/** A refused create: is the ID taken by another iPad? Checked once with signal. */
const takenCheck = new Map<string, "taken" | "free" | "checking">();
/** Refusals already told in the bar below the sheet. */
const seenRefused = new Set<string>();
// The new row and the rows still to come in a run (Copy Down To, Bring In), kept on this iPad so a reload does not
// lose them (B3). The keys live in run-store.ts: Field Labels reads them for its push count.
const CALCS_KEY = "st_asset_id_calcs";
const ZOOM_KEY = "st_asset_id_zoom";
/** Aa steps: 100%, 90%, 80%, 70%, then back to 100%. */
export const ZOOMS = [1, 0.9, 0.8, 0.7] as const;

function zoomRemembered(): number {
  try {
    const z = Number(window.localStorage.getItem(ZOOM_KEY));
    return (ZOOMS as readonly number[]).includes(z) ? z : 1;
  } catch {
    return 1;
  }
}

function calcsRemembered(): boolean {
  try {
    return window.localStorage.getItem(CALCS_KEY) === "on";
  } catch {
    return false;
  }
}

// ---- Google Drive (SITE_FOLDERS_WORDS.md rev 3.1, MOCKUP_DRIVE_FOLDERS.html rev 2 panels B and C) ----
/** settings/drive. Undefined until read; null when Drive was never set up. */
let driveSet: Partial<DriveSettings> | null | undefined;
let siteFolder: SurveySite["driveFolder"] = null;
/** A Build Folder ask on this iPad or at the server, not done yet. */
let folderAsk: { id: string; reason: string } | null = null;
let unsubs: (() => void)[] = [];
let driveFor = "";
let driveReadAt = 0;

function driveView() {
  return { settings: driveSet ? { connected: driveSet.connected === true, rootFolderId: String(driveSet.rootFolderId ?? "") } : null };
}

/** Reads Drive once per site and watches the site's folder. Never blocks the sheet. */
function startDrive(company: string, site: string): void {
  if (driveFor === `${company}/${site}`) {
    // Same site: read the connection again when it is old, so Connect Drive shows without closing the app.
    if (driveReadDue(driveReadAt, Date.now())) {
      driveReadAt = Date.now();
      void loadDriveSettings(company).then((d) => {
        if (JSON.stringify(d) === JSON.stringify(driveSet)) return;
        driveSet = d;
        schedulePaint();
      });
    }
    return;
  }
  driveFor = `${company}/${site}`;
  for (const u of unsubs) u();
  unsubs = [];
  driveSet = undefined;
  siteFolder = null;
  folderAsk = null;
  driveReadAt = Date.now();
  void loadDriveSettings(company).then((d) => {
    driveSet = d;
    schedulePaint();
  });
  unsubs.push(
    watchSiteDriveFolder(company, site, (f) => {
      siteFolder = f;
      if (f?.id) folderAsk = null;
      schedulePaint();
    }),
  );
}

/** Build Folder: a small record through the Outbox (rule 34). Offline it waits and lands with signal. */
async function askForFolder(): Promise<void> {
  if (!host || !st || folderAsk) return;
  const company = host.badge.company;
  const site = st.pack.siteNumber;
  const t = new Date();
  const id = `${t.toISOString().replace(/\D/g, "").slice(0, 17)}-${Math.random().toString(36).slice(2, 6).padEnd(4, "0")}`;
  folderAsk = { id, reason: "" };
  paint();
  await saveRecord({
    path: `companies/${company}/sites/${site}/driveRequests/${id}`,
    company,
    siteNumber: site,
    assetId: "Drive folder",
    card: "Build Drive folder",
    data: { kind: "folder", at: t.toISOString(), by: host.badge.badgeId, byName: host.badge.holderName },
    savedAt: t.toISOString(),
  });
  unsubs.push(
    watchDriveRequest(company, site, id, (state, reason) => {
      if (state === "failed" && folderAsk?.id === id) {
        folderAsk = null;
        if (st) st.flash = { text: `Build Folder did not finish. ${reason}`, tone: "red" };
        schedulePaint();
      }
    }),
  );
}

function chipNow(): string {
  const view = driveView();
  const has = siteHasFolder(view, siteFolder);
  return driveChip({ view, folderUrl: has && siteFolder ? siteFolder.url : null, waiting: Boolean(folderAsk), reason: "" });
}

const assetsPath = (company: string, site: string) => `companies/${company}/sites/${site}/assets/`;

// ---- The pack: what this page reads and adds to ----

function docs(pack: SitePack): Record<string, Record<string, unknown>> {
  if (!pack.assetDocs) {
    // An older device copy: build what the page can from the Map's assets until the pack refreshes.
    pack.assetDocs = Object.fromEntries(pack.assets.map((a) => [a.assetId, { ...a, isRemoved: false } as Record<string, unknown>]));
  }
  return pack.assetDocs;
}

/** The Equipment Type dropdown (asset-id.ts, shared with the Field Labels push). */
export { classesOf };

/** Puts one saved record into the pack, so the Map, Collect and scan see it at once. */
function applyToPack(pack: SitePack, assetId: string, data: Record<string, unknown>): void {
  const all = docs(pack);
  const merged = { ...(all[assetId] ?? {}), ...data };
  all[assetId] = merged;
  const i = pack.assets.findIndex((a) => a.assetId === assetId);
  if (merged.isRemoved === true) {
    if (i >= 0) pack.assets.splice(i, 1);
    return;
  }
  const a = packAssetOf(assetId, merged);
  if (i >= 0) pack.assets[i] = a;
  else {
    pack.assets.push(a);
    pack.assets.sort((x, y) => x.assetId.localeCompare(y.assetId, undefined, { numeric: true }));
  }
}

/**
 * Saves made on this iPad lie over the pack, so a pack opened with no signal (or refreshed before
 * they sent) still holds them. survey-page.ts calls this every time it reads a pack.
 */
export async function overlayLocalAssets(pack: SitePack, company: string): Promise<void> {
  const prefix = assetsPath(company, pack.siteNumber);
  try {
    const mine = (await deviceList.records()).filter((r) => r.path.startsWith(prefix) && r.state !== "sent").sort((a, b) => a.savedAt.localeCompare(b.savedAt));
    for (const r of mine) applyToPack(pack, r.path.slice(prefix.length), r.data);
  } catch {
    // No device list (private window). The pack stands.
  }
}

// ---- Open ----

export function drawAssetIds(h: IdsHost, pack: SitePack, room: string | null, row: string | null = null, pushRoom: string | null = null): void {
  host = h;
  wire();
  startDrive(h.badge.company, pack.siteNumber);
  if (!st || st.pack !== pack) {
    const draft = rememberedDraft(h.badge.company, pack.siteNumber);
    const queue = rememberedRun(h.badge.company, pack.siteNumber);
    // A new row started before the app closed comes back at the bottom, with the rest of its run. Otherwise there is no new row.
    const started = Boolean(draft.type || draft.locNum || draft.pos || draft.loc);
    st = { pack, draft, pick: null, flash: null, find: "", sheet: null, showRemoved: false, fromMap: false, scrollTo: false, after: null, check: null, queue: started ? queue : [], calcs: calcsRemembered(), menu: null, run: started && queue.length > 0, hasDraft: started, above: null, zoom: zoomRemembered(), list: null, def: null };
    void refreshLocal();
    void carryQrMarks();
  }
  if (pushRoom !== null) {
    // Push ER1, tapped on Field Labels once ER1 is marked Done (PUSH_BY_ROOM_B7_WORDS.md items 1 to 3). Once only;
    // the address drops it. No room in the address (an old link): the whole walk, as before.
    st.sheet = { kind: "bringIn", room: pushRoom || null };
    location.replace(`#/assets/${pack.siteNumber}`);
  }
  if (row) {
    // Change In Creator from the asset page: that row picked and in view. Once only; the address drops it.
    if (pack.assets.some((a) => a.assetId === row) || pack.assetDocs?.[row]) {
      st.pick = row;
      st.scrollTo = true;
    }
    location.replace(`#/assets/${pack.siteNumber}`);
  }
  if (room) {
    // Add Asset from the Map: the blank row, in that room when the Map knows it. Once only; the address
    // drops the room so a repaint keeps your typing.
    const known = room !== NEW;
    st.draft = known ? formForRoom(room, st.draft) : { ...st.draft, type: "", num: "", pos: "" };
    st.pick = NEW;
    st.hasDraft = true;
    st.fromMap = true;
    st.scrollTo = true;
    st.after = null;
    st.check = null;
    st.queue = [];
    st.flash = { text: `Add an asset${known ? ` in ${room}` : ""} in the new row at the bottom. It saves itself when every answer is in. Back To Map places it.`, tone: "amber" };
    rememberDraft();
    location.replace(`#/assets/${pack.siteNumber}`);
    return;
  }
  paint();
}

function rememberedDraft(company: string, site: string): IdForm {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY(company, site));
    if (raw) return { ...BLANK_FORM, ...(JSON.parse(raw) as Partial<IdForm>) };
  } catch {
    // Nothing remembered.
  }
  return { ...BLANK_FORM };
}

function rememberedRun(company: string, site: string): IdForm[] {
  try {
    const raw = window.localStorage.getItem(RUN_KEY(company, site));
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? list.filter((f) => f && typeof f === "object").map((f) => ({ ...BLANK_FORM, ...(f as Partial<IdForm>) })) : [];
  } catch {
    return [];
  }
}

function rememberDraft(): void {
  if (!st || !host) return;
  try {
    const runKey = RUN_KEY(host.badge.company, st.pack.siteNumber);
    if (st.hasDraft && st.queue.length) window.localStorage.setItem(runKey, JSON.stringify(st.queue));
    else window.localStorage.removeItem(runKey);
  } catch {
    // Storage full or blocked. The rows still to come stay in memory only.
  }
  try {
    window.localStorage.setItem(DRAFT_KEY(host.badge.company, st.pack.siteNumber), JSON.stringify(st.draft));
  } catch {
    // Storage full or blocked. The blank row is not remembered.
  }
}

async function refreshLocal(): Promise<void> {
  if (!host || !st) return;
  const prefix = assetsPath(host.badge.company, st.pack.siteNumber);
  try {
    local = (await deviceList.records()).filter((r) => r.path.startsWith(prefix));
  } catch {
    local = [];
  }
  for (const r of local) if (r.state === "refused" && isCreate(r.data) && !takenCheck.has(r.assetId)) void checkTaken(r);
  const fresh = local.filter((r) => r.state === "refused" && !seenRefused.has(`${r.path}|${r.savedAt}`));
  for (const r of fresh) seenRefused.add(`${r.path}|${r.savedAt}`);
  if (fresh.length && st) st.flash = { text: `The cloud refused ${fresh.map((r) => r.assetId).join(", ")}. See the red line above the sheet.`, tone: "red" };
  if (onThisPage()) schedulePaint();
}

const isCreate = (d: Record<string, unknown>) => typeof d.createdAt === "string" && d.source === "Asset ID page";

async function checkTaken(r: RecordEntry): Promise<void> {
  if (!host || !isOnline()) return;
  takenCheck.set(r.assetId, "checking");
  try {
    const cloud = await cloudAsset(host.badge.company, r.siteNumber, r.assetId);
    takenCheck.set(r.assetId, cloud && cloud.createdAt !== r.data.createdAt ? "taken" : "free");
  } catch {
    takenCheck.delete(r.assetId);
  }
  if (onThisPage()) paint();
}

function onThisPage(): boolean {
  // Field Labels (#/assets/{site}/labels) is its own page with its own events.
  return /^#\/assets\/[^/]+/.test(location.hash) && !/^#\/assets\/[^/]+\/labels(\/|$)/.test(location.hash);
}

// ---- What the sheet shows ----

function existingIds(): Set<string> {
  return new Set(Object.keys(docs(st!.pack)));
}

function mappedIds(pack: SitePack): Set<string> {
  const out = new Set<string>();
  for (const l of pack.links) {
    if (l.status === "Removed") continue;
    if (l.fromAsset) out.add(l.fromAsset);
    if (l.toAsset) out.add(l.toAsset);
  }
  for (const t of pack.modelTags ?? []) out.add(String(t["Asset ID"] ?? "").split(".")[0]!);
  for (const id of Object.keys(pack.ieaRecords ?? {})) out.add(id);
  return out;
}

function statusOf(assetId: string): RowStatus {
  const mine = local.find((r) => r.assetId === assetId && r.state !== "sent");
  if (mine?.state === "refused") return "bad";
  if (mine?.state === "waiting") return "wait";
  return "saved";
}

function notPrinted(d: Record<string, unknown>): boolean {
  return d.isRemoved !== true && d.source === "Asset ID page" && !d.qrLabelPrintedAt && !shockLabelOnly(d);
}

/** The sheet's rows in the workbook's walk: outside first, then each room, each cabinet then its positions. */
function sheetRows(classes: readonly ClassOption[]): SheetRow[] {
  const s = st!;
  const all = docs(s.pack);
  const list: ListAsset[] = Object.entries(all)
    .filter(([, d]) => s.showRemoved || d.isRemoved !== true)
    .map(([assetId, d]) => ({ assetId, siteEquipmentTag: typeof d.siteEquipmentTag === "string" ? d.siteEquipmentTag : "", location: String(d.location ?? ""), equipmentType: String(d.equipmentType ?? ""), isRemoved: d.isRemoved === true }));
  const ordered: string[] = [];
  for (const room of groupAssets(list, s.find)) for (const hsg of room.housings) {
    if (hsg.self) ordered.push(hsg.self.assetId);
    for (const k of hsg.kids) ordered.push(k.assetId);
  }
  const mapped = mappedIds(s.pack);
  const rows: SheetRow[] = ordered.map((id, i) => ({
    // A walk row's name belongs to Field Labels (ASSESSMENT_FLOW_B7_PART2_WORDS.md item 8). Part 3 brings Change Asset ID.
    idEditable: canRetypeId(all[id]!, mapped.has(id)) && statusOf(id) !== "bad" && !fromWalk(all[id]),
    key: id,
    form: tagTyping?.key === id ? { ...formOfRecord(id, all[id]!, classes), tag: tagTyping.value } : formOfRecord(id, all[id]!, classes),
    assetId: id,
    status: statusOf(id),
    saved: true,
    removed: all[id]!.isRemoved === true,
    dupe: false,
    picked: s.pick === id,
    n: i + 1,
  }));
  const ids = existingIds();
  // A site with no rows has no row number to double-tap. It opens with the blank new row instead,
  // the same as a double-tap gives (Robert, 2026-10-27, Nicholasville). Not while Find hides the rows.
  if (!s.hasDraft && !s.find.trim() && !rows.some((r) => !r.removed)) {
    s.draft = { ...BLANK_FORM };
    s.hasDraft = true;
    s.after = null;
    s.check = null;
    s.queue = [];
    s.run = false;
    s.pick = NEW;
  }
  if (!s.hasDraft) {
    s.above = null;
    return rows;
  }
  const draftId = assetIdOf(s.pack.siteNumber, s.draft, classes);
  const dupe = Boolean(draftId) && ids.has(draftId);
  const fresh: SheetRow[] = [
    { key: NEW, form: s.draft, assetId: draftId, status: dupe ? "bad" : "draft", saved: false, removed: false, dupe, picked: s.pick === NEW, n: 0, check: s.check },
    ...s.queue.map((f, i): SheetRow => {
      const id = assetIdOf(s.pack.siteNumber, f, classes);
      return { key: `queue-${i}`, form: f, assetId: id, status: "draft", saved: false, removed: false, dupe: ids.has(id), picked: false, n: 0, queued: true };
    }),
  ];
  // Copy Down: the new row sits right under the row it came from, where you are working.
  const at = s.after ? rows.findIndex((r) => r.key === s.after) : -1;
  // Where a double-tap fills from: the saved row right above the new row.
  const aboveRow = (at >= 0 ? rows.slice(0, at + 1) : rows).filter((r) => !r.removed).pop();
  s.above = aboveRow?.key ?? null;
  if (at >= 0) rows.splice(at + 1, 0, ...fresh);
  else rows.push(...fresh);
  return rows;
}

function problems(): { assetId: string; text: string }[] {
  return local
    .filter((r) => r.state === "refused")
    .map((r) => {
      const taken = takenCheck.get(r.assetId) === "taken";
      const text = isCreate(r.data)
        ? taken
          ? `Another iPad made ${r.assetId} first. The cloud kept theirs. Renumber yours, or drop it.`
          : `The cloud refused ${r.assetId}. ${r.reason || ""} Renumber it, or drop it.`
        : `The cloud refused a change to ${r.assetId}. ${r.reason || ""}`;
      return { assetId: r.assetId, text: text.replace(/\s+/g, " ").trim() };
    });
}

function initialRa(): string {
  const dates = Object.values(docs(st!.pack))
    .map((d) => String(d.createdAt ?? ""))
    .filter((x) => /^\d{4}-\d{2}-\d{2}/.test(x))
    .sort();
  if (!dates.length) return "";
  const d = new Date(dates[0]!);
  return Number.isNaN(d.getTime()) ? dates[0]!.slice(0, 10) : `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

/** The picked saved row's form, or null. */
function pickedForm(classes: readonly ClassOption[]): { id: string; form: IdForm; doc: Record<string, unknown> } | null {
  const s = st!;
  if (!s.pick || s.pick === NEW) return null;
  const doc = docs(s.pack)[s.pick];
  return doc ? { id: s.pick, form: formOfRecord(s.pick, doc, classes), doc } : null;
}

function paint(): void {
  // Work that finishes after leaving (a PDF, a Drive retry) never paints over another page.
  if (!host || !st || !onThisPage()) return;
  const h = host;
  const s = st;
  const site = s.pack.siteNumber;
  const classes = classesOf(s.pack);
  const src = copySource(classes);
  const listForm = s.list ? (s.list.key === NEW ? s.draft : docs(s.pack)[s.list.key] ? formOfRecord(s.list.key, docs(s.pack)[s.list.key]!, classes) : null) : null;
  const menuDoc = s.menu && s.menu !== NEW ? docs(s.pack)[s.menu] : null;
  const html = creatorPage({
    siteNumber: site,
    siteName: h.siteName,
    driveChip: chipNow(),
    siteIdent: siteIdentifier(site, h.siteName),
    auditor: h.badge.holderName,
    initialRa: initialRa(),
    online: isOnline(),
    find: s.find,
    canMap: h.canMap,
    fromMap: s.fromMap,
    notPrinted: Object.values(docs(s.pack)).filter(notPrinted).length,
    packBusy: h.packBusy(),
    classes,
    rows: sheetRows(classes),
    problems: problems(),
    calcs: s.calcs,
    menu: s.menu
      ? { key: s.menu, label: s.menu === NEW ? "The new row" : `Row ${s.menu.replace(/^\d{2}-/, "")}`, draft: s.menu === NEW, removed: menuDoc?.isRemoved === true, walk: fromWalk(menuDoc) }
      : null,
    // Only what needs you shows. Saves show as the green dot on the row number.
    line: s.flash && s.flash.tone !== "green" ? { text: s.flash.text, tone: s.flash.tone } : null,
    canDownTo: Boolean(src && assetIdOf(site, src.form, classes) && copyDownForm(src.form) && (!src.draft || !missingFields(src.form).some((m) => !ANSWER_LABELS.has(m)))),
    showRemoved: s.showRemoved,
    zoom: s.zoom,
    list:
      s.list && listForm
        ? {
            key: s.list.key,
            cell: s.list.cell,
            title: CELL_TITLES[s.list.cell] ?? "",
            values: listValues(s.list.cell, classes),
            current: listForm[s.list.cell],
            clear: s.list.key === NEW && Boolean(listForm[s.list.cell]),
            left: s.list.left,
            top: s.list.top,
          }
        : null,
    def: s.def,
    isAdmin: h.badge.authorities.includes("admin"),
  });
  // Keep the cell being typed in, the caret, and where the sheet is scrolled, across the repaint.
  const active = document.activeElement as HTMLInputElement | null;
  const focusId = active?.id && h.root.contains(active) ? active.id : "";
  let caret: number | null = null;
  try {
    caret = active?.selectionStart ?? null;
  } catch {
    caret = null;
  }
  const wrapNow = h.root.querySelector<HTMLElement>("#ac-wrap");
  const sx = wrapNow?.scrollLeft ?? 0;
  const sy = wrapNow?.scrollTop ?? null;
  // Swapping the page fires focusout on the cell being typed in. That is not the person leaving it.
  painting = true;
  try {
    h.frame(html + sheetHtml(classes), "");
  } finally {
    painting = false;
  }
  const wrap = h.root.querySelector<HTMLElement>("#ac-wrap");
  if (wrap) {
    fitHeight(wrap);
    wrap.scrollLeft = sx;
    // Opening: the last rows in view, not the open space under them. After that the sheet stays where you left it.
    if (sy === null) {
      const table = wrap.querySelector<HTMLElement>(".ac-sheet");
      wrap.scrollTop = Math.max(0, (table?.getBoundingClientRect().height ?? 0) - wrap.clientHeight + 40);
    } else wrap.scrollTop = Math.round(sy * zoomRatio);
    zoomRatio = 1;
    if (s.scrollTo && s.pick) {
      s.scrollTo = false;
      const tr = wrap.querySelector<HTMLElement>(`tr[data-row="${CSS.escape(s.pick)}"]`);
      tr?.scrollIntoView({ block: "nearest" });
    }
  }
  if (focusId) {
    const el = h.root.querySelector<HTMLInputElement>(`#${CSS.escape(focusId)}`);
    if (el) {
      el.focus({ preventScroll: true });
      if (caret !== null && el.tagName === "INPUT") {
        try {
          el.setSelectionRange(caret, caret);
        } catch {
          // Not a text field.
        }
      }
    }
  }
}

/** The sheet fills the screen to the bottom, like the workbook window. No bar below it (rev 6). */
function fitHeight(wrap: HTMLElement): void {
  const top = wrap.getBoundingClientRect().top;
  wrap.style.height = `${Math.max(300, window.innerHeight - top)}px`;
}

/** Aa changed the size: keep the same rows in view. */
let zoomRatio = 1;

const CELL_TITLES: Partial<Record<keyof IdForm, string>> = { loc: "Equipment Location", type: "Equipment Type", volt: "Nominal System Voltage", cond: "Conductor", oos: "Out of Service" };

function sheetHtml(classes: readonly ClassOption[]): string {
  if (!st?.sheet) return "";
  const s = st;
  const sh = s.sheet!;
  if (sh.kind === "downTo") {
    const src = copySource(classes);
    const first = src ? copyDownForm(src.form) : null;
    if (!src || !first) return "";
    const ids = existingIds();
    const field = steppedField(src.form);
    return downToSheet({
      source: assetIdOf(s.pack.siteNumber, src.form, classes),
      field,
      from: padPart(first[field]),
      to: sh.to,
      ids: copyDownSeries(src.form, sh.to).map((f) => {
        const id = assetIdOf(s.pack.siteNumber, f, classes);
        return { id, exists: ids.has(id) };
      }),
    });
  }
  if (sh.kind === "bringIn") {
    const b = bringInNow(classes, sh.room);
    return bringInSheet({ room: sh.room, count: b.rows.length, already: b.already, waiting: b.waiting, byRoom: b.byRoom, skipped: b.skipped, walked: b.walked, remove: b.remove, restore: b.restore, goneRemove: b.goneRemove, goneRestore: b.goneRestore });
  }
  if (sh.kind === "remove") {
    const d = docs(s.pack)[sh.id];
    if (!d) return "";
    return removeSheet({
      assetId: sh.id,
      warning: removeWarning({ mapped: mappedIds(s.pack).has(sh.id), studied: Boolean(d.afStudyId || typeof d.incidentEnergyCal === "number"), printed: Boolean(d.qrLabelPrintedAt) }),
    });
  }
  return qrCardHtml(sh);
}

// ---- The QR print card (rev 5: one PDF in Drive, 10_QR_Labels.pdf) ----

type PrintSheet = Extract<Sheet, { kind: "print" }>;

/** The walk on this iPad, as Field Labels shows it. */
function walkHere(): FieldSite {
  const read = (k: string): string | null => {
    try {
      return window.localStorage.getItem(k);
    } catch {
      return null;
    }
  };
  return loadFieldWalkHere(read, host!.badge.company, st!.pack.siteNumber).data;
}

/**
 * The one roll, the same as Field Labels' QR block (qr-roll.ts, ASSESSMENT_FLOW_B5_WORDS.md part 1b): every live
 * asset and every Asset ID the walk sets that is not on the sheet yet, room by room in ID order. One 00_QR_Labels.pdf
 * holds both, whichever card updates it.
 */
function qrRoll(): PcRoll {
  return qrRollOf(qrEntries({ walk: walkHere(), siteNumber: st!.pack.siteNumber, classes: classesOf(st!.pack), docs: docs(st!.pack) }), st!.pack.siteNumber);
}

/**
 * QR labels printed from Field Labels before or after their row saved are marked in the walk. A saved row that the
 * walk marks printed and the sheet does not takes the stamp, with its history, the way this card's own print does.
 */
async function carryQrMarks(): Promise<void> {
  const s = st;
  if (!s || !host) return;
  const printed = walkHere().printed ?? {};
  const all = docs(s.pack);
  const todo = Object.keys(all).filter((id) => all[id]!.isRemoved !== true && !all[id]!.qrLabelPrintedAt && printed[qrPrintKey(id)]);
  if (!todo.length) return;
  const t = stamp();
  bulk = true;
  try {
    for (const id of todo) await saveAsset(id, { qrLabelPrintedAt: printed[qrPrintKey(id)]!.at, updatedAt: t.at, updatedBy: t.badgeId, updatedByName: t.holderName }, "Label printed");
  } finally {
    bulk = false;
  }
  if (onThisPage()) schedulePaint();
}

function qrCardHtml(sh: PrintSheet): string {
  const roll = qrRoll();
  seedPicks(sh.pc, [roll]);
  return printCard({ title: "Print QR Labels", roll: "qrRoll", rolls: [roll], state: sh.pc });
}

/** A tap on the card. */
async function qrCardClick(el: HTMLElement): Promise<void> {
  const s = st!;
  const sh = s.sheet;
  if (sh?.kind !== "print") return;
  const roll = qrRoll();
  const a = cardClick(el, sh.pc, [roll]);
  if (!a) return;
  switch (a.act) {
    case "close":
      if (sh.pc.busy) return;
      stopMasterWatches();
      s.sheet = null;
      break;
    case "setup":
      stopMasterWatches();
      s.sheet = null;
      location.hash = "#/settings/printer";
      return;
    case "update":
    case "updateall":
      await updateMaster(sh, roll);
      return;
    case "make":
      await makePdf(sh, roll);
      return;
    case "share": {
      const f = sh.pc.small.get(roll.key)?.file;
      if (f) await shareFile(f);
      return;
    }
    case "download": {
      const f = sh.pc.small.get(roll.key)?.file;
      if (f) downloadFile(f);
      return;
    }
    case "mdownload": {
      const f = sh.pc.masterFiles.get(roll.key);
      if (f) downloadFile(f);
      return;
    }
    case "retry":
      void retryMaster(sh.pc, s.pack.siteNumber, roll.driveName, schedulePaint);
      return;
    default:
      break;
  }
  paint();
}

// ---- Saving ----

function stamp(): { siteNumber: string; badgeId: string; holderName: string; at: string } {
  return { siteNumber: st!.pack.siteNumber, badgeId: host!.badge.badgeId, holderName: host!.badge.holderName, at: new Date().toISOString() };
}

/**
 * One save into the Outbox. A change to an asset whose earlier save has not sent yet joins it, so
 * nothing is lost: an edit to a new asset that is still waiting rides in with its create.
 */
async function saveAsset(assetId: string, data: Record<string, unknown>, card: string, note = ""): Promise<void> {
  const h = host!;
  const s = st!;
  const path = `${assetsPath(h.badge.company, s.pack.siteNumber)}${assetId}`;
  let merged = data;
  try {
    const before = (await deviceList.records()).find((r) => r.path === path && r.state !== "sent");
    if (before) {
      if (isCreate(before.data)) {
        const { updatedAt: _a, updatedBy: _b, updatedByName: _c, ...rest } = data;
        merged = { ...before.data, ...rest };
        card = "New asset";
      } else merged = { ...before.data, ...data };
    }
  } catch {
    // No device list: send what this save holds.
  }
  const prior = docs(s.pack)[assetId];
  const before = prior ? { ...prior } : null;
  applyToPack(s.pack, assetId, data);
  await saveRecord({ path, company: h.badge.company, siteNumber: s.pack.siteNumber, assetId, card, data: merged, savedAt: new Date().toISOString() });
  // Every change also leaves one history record that is never edited (rule 32).
  await saveChange(assetId, actionOf(before, data), changesOf(before, data), note);
  h.assetsChanged();
}

/** One history record through the Outbox: what changed, who, which device, when. */
async function saveChange(assetId: string, action: ChangeAction, changes: Record<string, { before: unknown; after: unknown }>, note = ""): Promise<void> {
  const h = host!;
  const site = st!.pack.siteNumber;
  const t = { ...stamp(), deviceName: h.deviceName() || "Not named" };
  const id = changeIdOf(assetId, t.at);
  await saveRecord({
    path: `companies/${h.badge.company}/sites/${site}/assetChanges/${id}`,
    company: h.badge.company,
    siteNumber: site,
    assetId,
    card: `History: ${action}`,
    data: changeRecordOf(assetId, action, changes, t, note),
    savedAt: t.at,
  });
}

/** True when the walk on this iPad makes this Asset ID: the row came from Field Labels (ASSESSMENT_FLOW_B7_PART2_WORDS.md). */
function walkMakes(id: string, classes: readonly ClassOption[]): boolean {
  return walkRowsOf(walkHere(), st!.pack.siteNumber, classes).rows.some((w) => w.id === id);
}

/** A new row: saved the moment it is complete and its Asset ID is new. True when it saved. */
async function createRow(f: IdForm): Promise<string | null> {
  const s = st!;
  const classes = classesOf(s.pack);
  const id = assetIdOf(s.pack.siteNumber, f, classes);
  if (!id || existingIds().has(id) || missingFields(f).length) return null;
  await saveAsset(id, newAssetRecord(id, f, classes, stamp(), walkMakes(id, classes)), "New asset");
  return id;
}

/** The four field answers' labels, as missingFields names them. */
const ANSWER_LABELS = new Set(["Passive Exposure", "Active Exposure", "Properly Installed", "Used Per Manufacturer Instructions"]);

/** A new row with something typed in it that has not saved. */
function unfinishedDraft(): boolean {
  const d = st!.draft;
  return Boolean(d.type || d.locNum || d.pos || (d.loc && d.loc !== BLANK_FORM.loc));
}

/** What Copy Down copies from: the new row when you are on it, else the picked saved row. */
function copySource(classes: readonly ClassOption[]): { id: string; form: IdForm; draft: boolean } | null {
  const s = st!;
  if (s.pick === NEW) return s.hasDraft ? { id: assetIdOf(s.pack.siteNumber, s.draft, classes), form: s.draft, draft: true } : null;
  const p = pickedForm(classes);
  return p && p.doc.isRemoved !== true ? { id: p.id, form: p.form, draft: false } : null;
}

/**
 * Saves the new row if it is complete and its Asset ID is new. The next Copy Down To row (if any) takes
 * its place right under it; otherwise a blank row waits at the bottom. Returns the saved ID, or null.
 */
async function commitDraft(): Promise<string | null> {
  const s = st!;
  const id = await createRow(s.draft);
  if (!id) return null;
  const nextUp = s.queue.shift();
  if (nextUp) {
    s.draft = nextUp;
    s.after = id;
    s.check = null;
  } else {
    // No row waits after a save. The next one comes from a double-tap on a row number.
    s.draft = { ...BLANK_FORM };
    s.hasDraft = false;
    s.after = null;
    s.check = null;
  }
  rememberDraft();
  // A row pushed from the walk whose QR label printed at the gear takes that stamp now.
  void carryQrMarks();
  return id;
}

/** Moving off the new row: it saves if it can. If not, a line says what it still needs. */
async function leaveDraft(): Promise<void> {
  const s = st!;
  if (!s.hasDraft) return;
  const id = await commitDraft();
  const classes = classesOf(s.pack);
  if (id) s.flash = { text: `Saved ${id}.${isOnline() ? "" : " No signal: it waits in the Outbox and is ready to map now."}`, tone: "green" };
  else if (assetIdOf(s.pack.siteNumber, s.draft, classes) && missingFields(s.draft).length)
    s.flash = { text: `The new row is not done: ${missingFields(s.draft).join(", ")}.`, tone: "amber" };
}

/**
 * The new row changed. It saves the moment it is complete and its Asset ID is new (rev 6 section 2).
 * In a Copy Down To run the next row comes up in its place. Nothing scrolls.
 */
async function settleDraft(quiet = false): Promise<void> {
  const s = st!;
  if (quiet && !(s.hasDraft && !missingFields(s.draft).length)) return;
  if (s.hasDraft && !missingFields(s.draft).length) {
    const hadNext = s.queue.length > 0;
    const id = await commitDraft();
    if (id) {
      if (hadNext) s.pick = NEW;
      else {
        s.run = false;
        s.pick = id;
      }
      s.flash = isOnline() ? null : { text: `${id} waits in the Outbox: no signal. It is ready to map now.`, tone: "amber" };
    } else {
      const dupe = assetIdOf(s.pack.siteNumber, s.draft, classesOf(s.pack));
      if (dupe && existingIds().has(dupe)) s.flash = { text: `${dupe.replace(/^\d{2}-/, "")} already exists. Change the red cell.`, tone: "red" };
    }
  }
  paint();
}

/** A new type or room on the new row: the next free number there, no END Location. The number is outlined to check. */
function restartNumber(cell: keyof IdForm): void {
  const s = st!;
  if (!["type", "loc", "locNum"].includes(cell) || !s.draft.type) return;
  s.draft = { ...s.draft, num: nextFreeNumber(s.pack.siteNumber, s.draft, classesOf(s.pack), existingIds()), pos: "" };
  s.check = "num";
}

/** A saved row's shock answer or Out of Service changed: it saves itself. */
async function editRow(assetId: string, key: keyof IdForm, value: string): Promise<void> {
  const s = st!;
  const classes = classesOf(s.pack);
  const doc = docs(s.pack)[assetId];
  if (!doc) return;
  const was = formOfRecord(assetId, doc, classes);
  // Active Exposure carries Passive with it (2026-10-23).
  const f = key === "active" ? withExposure(was, value) : { ...was, [key]: value };
  const rec = editRecord(doc, f, classes, stamp());
  if (!rec) return paint();
  await saveAsset(assetId, rec, "Asset edit");
  s.flash = { text: `Saved the change to ${assetId}.`, tone: "green" };
  paint();
}

/**
 * A saved row's Asset ID retyped before anything carries it (no label printed, not mapped). The new ID is
 * made with every answer, and the old one is removed. Both leave history records.
 */
async function renameRow(oldId: string, key: keyof IdForm, value: string): Promise<void> {
  const s = st!;
  const classes = classesOf(s.pack);
  const doc = docs(s.pack)[oldId];
  if (!doc || !canRetypeId(doc, mappedIds(s.pack).has(oldId))) return paint();
  if (fromWalk(doc)) {
    s.flash = { text: `${oldId} came from Field Labels. Rename it there, then Update Push.`, tone: "amber" };
    return paint();
  }
  let f: IdForm = { ...formOfRecord(oldId, doc, classes), [key]: value };
  if (f.loc === "Outside") f = { ...f, locNum: "", pos: "" };
  const newId = assetIdOf(s.pack.siteNumber, f, classes);
  if (!newId || newId === oldId) return paint();
  if (existingIds().has(newId)) {
    s.flash = { text: `${newId} already exists. ${oldId} is unchanged.`, tone: "red" };
    return paint();
  }
  if (missingFields(f).length) {
    s.flash = { text: `${oldId} needs ${missingFields(f).join(", ")} before its ID can change.`, tone: "amber" };
    return paint();
  }
  const t = stamp();
  bulk = true;
  try {
    await saveAsset(newId, newAssetRecord(newId, f, classes, t, walkMakes(newId, classes)), "New asset");
    await saveAsset(oldId, { isRemoved: true, updatedAt: t.at, updatedBy: t.badgeId, updatedByName: t.holderName }, "Asset removed");
  } finally {
    bulk = false;
  }
  if (s.after === oldId) s.after = newId;
  s.pick = newId;
  s.flash = { text: `Row is now ${newId}. ${oldId} is removed: no label was printed and nothing was mapped under it.`, tone: "green" };
  paint();
}

/**
 * Double-tap a row number: a blank row right under it (rev 6, Robert 2026-10-22 5:07 PM).
 * The one other way: a site with no rows opens with the blank row already there (sheetRows).
 */
function addRowUnder(key: string): void {
  const s = st!;
  if (key === NEW || key.startsWith("queue-")) return;
  if (s.hasDraft && unfinishedDraft()) {
    s.flash = { text: "Finish the new row first, or press and hold its number to clear it.", tone: "amber" };
    return paint();
  }
  s.draft = { ...BLANK_FORM };
  s.hasDraft = true;
  s.after = key;
  s.check = null;
  s.queue = [];
  s.run = false;
  s.pick = NEW;
  s.flash = null;
  rememberDraft();
  paint();
}

/** The saved row right above the new row, as a form. */
function aboveForm(): IdForm | null {
  const s = st!;
  const d = s.above ? docs(s.pack)[s.above] : null;
  return d && s.above ? formOfRecord(s.above, d, classesOf(s.pack)) : null;
}

/** Double-tap an empty cell on the new row: it fills from the row above. True when it filled. Does not paint. */
function fillCell(cell: FillField): boolean {
  const s = st!;
  const a = aboveForm();
  if (!a) {
    s.flash = { text: "Nothing above to copy.", tone: "amber" };
    return false;
  }
  const classes = classesOf(s.pack);
  const r = fillFromAbove(cell, s.draft, a, () => nextFreeNumber(s.pack.siteNumber, s.draft, classes, existingIds()));
  if (r.value === null) {
    s.flash = { text: r.why ?? "Nothing to copy.", tone: "amber" };
    return false;
  }
  s.draft = { ...s.draft, [cell]: r.value };
  if (cell === "loc" && r.value === "Outside") s.draft = { ...s.draft, locNum: "", pos: "" };
  if (r.stepped) s.check = cell;
  else if (s.check === cell) s.check = null;
  s.pick = NEW;
  s.flash = null;
  rememberDraft();
  return true;
}

/** Double-tap the empty Asset ID: the whole row from above, one cell stepped and outlined red. The four stay as they are. */
function fillRow(): void {
  const s = st!;
  const a = aboveForm();
  const next = a ? copyDownForm(a) : null;
  if (!a || !next) {
    s.flash = { text: "Nothing above to copy down.", tone: "amber" };
    return paint();
  }
  s.draft = { ...next, passive: s.draft.passive, active: s.draft.active, installed: s.draft.installed, usedPer: s.draft.usedPer };
  s.check = steppedField(a);
  s.pick = NEW;
  s.flash = null;
  rememberDraft();
  void settleDraft();
}

/** One cell's value set from its list (or a saved row's list). Same as the workbook's dropdown. */
function setCell(key: string, cell: keyof IdForm, value: string): void {
  const s = st!;
  if (key === NEW) {
    s.draft = { ...s.draft, [cell]: value };
    if (cell === "loc" && s.draft.loc === "Outside") s.draft = { ...s.draft, locNum: "", pos: "" };
    if (s.check === cell) s.check = null;
    if (value) restartNumber(cell);
    s.pick = NEW;
    rememberDraft();
    void settleDraft();
  } else if ((ID_FIELDS as readonly string[]).includes(cell)) void renameRow(key, cell, value);
  else void editRow(key, cell, value);
}

/** Opens a list cell's list under the cell. */
function openList(key: string, cell: keyof IdForm): void {
  const s = st!;
  const td = host?.root.querySelector<HTMLElement>(`td[data-ai="cell"][data-key="${CSS.escape(key)}"][data-cell="${cell}"]`);
  if (!td) return;
  const b = td.getBoundingClientRect();
  const left = Math.max(8, Math.min(b.left, window.innerWidth - 260));
  const top = b.bottom + 324 > window.innerHeight ? Math.max(8, b.top - 324) : b.bottom + 4;
  s.list = { key, cell, left, top };
  paint();
}

async function setRemoved(id: string, removed: boolean): Promise<void> {
  const s = st!;
  if (!id || id === NEW || !docs(s.pack)[id]) return;
  const t = stamp();
  await saveAsset(id, { isRemoved: removed, updatedAt: t.at, updatedBy: t.badgeId, updatedByName: t.holderName }, removed ? "Asset removed" : "Asset restored");
  s.flash = { text: removed ? `${id} is removed. It leaves the sheet and the map. Show Removed brings it back.` : `${id} is back.`, tone: removed ? "amber" : "green" };
  if (removed && !s.showRemoved && s.pick === id) s.pick = null;
  if (s.after === id) s.after = null;
  paint();
}

/**
 * The walk on this iPad, as Field Labels shows it, turned into new rows. Rows here now and the run are left out.
 * A room: that room only, plus removals and put backs from rooms no longer in the walk (PUSH_BY_ROOM_B7_WORDS.md
 * items 1, 2 and 10). No room: the whole walk.
 */
function bringInNow(classes: readonly ClassOption[], room: string | null): ReturnType<typeof bringIn> & { walked: boolean; waiting: number; remove: string[]; restore: string[]; goneRemove: string[]; goneRestore: string[] } {
  const s = st!;
  const read = (k: string): string | null => {
    try {
      return window.localStorage.getItem(k);
    } catch {
      return null;
    }
  };
  const whole = loadFieldWalkHere(read, host!.badge.company, s.pack.siteNumber).data;
  const walk = room ? roomOnly(whole, room) : whole;
  const onSheet = existingIds();
  const lined = new Set<string>();
  // Rows already lined up (the new row and its run) are left out too, so a second tap never doubles them.
  for (const f of s.hasDraft ? [s.draft, ...s.queue] : []) {
    const id = assetIdOf(s.pack.siteNumber, f, classes);
    if (id && !onSheet.has(id)) lined.add(id);
  }
  const b = bringIn(walk, s.pack.siteNumber, classes, onSheet);
  const rows = b.rows.filter((f) => !lined.has(assetIdOf(s.pack.siteNumber, f, classes)));
  const waiting = b.rows.length - rows.length;
  const byRoom = waiting ? bringIn(walk, s.pack.siteNumber, classes, new Set([...onSheet, ...lined])).byRoom : b.byRoom;
  // Removals and restores from the walk (ASSESSMENT_FLOW_B7_PART2_WORDS.md items 6 and 9): rows from Field Labels only.
  const ch = room ? roomChanges(whole, room, s.pack.siteNumber, classes, docs(s.pack)) : { ...pushChanges(whole, s.pack.siteNumber, classes, docs(s.pack)), goneRemove: [], goneRestore: [] };
  return { ...b, rows, byRoom, waiting, walked: walk.rooms.length > 0, remove: ch.remove, restore: ch.restore, goneRemove: ch.goneRemove, goneRestore: ch.goneRestore };
}

/**
 * Push: the new rows join the run at the bottom. A new row being filled stays first. Rows the walk took out are
 * marked removed, the same soft remove as Remove Row; rows the walk put back are restored with their answers
 * (ASSESSMENT_FLOW_B7_PART2_WORDS.md items 6, 7 and 9).
 */
async function bringInGo(): Promise<void> {
  const s = st!;
  const classes = classesOf(s.pack);
  const pushRoom = s.sheet?.kind === "bringIn" ? s.sheet.room : null;
  const b = bringInNow(classes, pushRoom);
  const { rows } = b;
  const remove = [...b.remove, ...b.goneRemove];
  const restore = [...b.restore, ...b.goneRestore];
  s.sheet = null;
  if (remove.length || restore.length) {
    const t = stamp();
    bulk = true;
    try {
      for (const id of remove) await saveAsset(id, { isRemoved: true, updatedAt: t.at, updatedBy: t.badgeId, updatedByName: t.holderName }, "Asset removed", "Removed in Field Labels");
      for (const id of restore) await saveAsset(id, { isRemoved: false, updatedAt: t.at, updatedBy: t.badgeId, updatedByName: t.holderName }, "Asset restored", "Back in Field Labels");
    } finally {
      bulk = false;
    }
    if (s.pick && remove.includes(s.pick) && !s.showRemoved) s.pick = null;
  }
  // The room is on the sheet now: its pills go green on every iPad (PUSH_BY_ROOM_B7_WORDS.md item 8).
  const said = pushRoom ? `${markRoomPushed(host!.badge.company, s.pack.siteNumber, pushRoom, classes, new Date().toISOString())} ` : "";
  const carried = [
    remove.length ? `${remove.length} row${remove.length === 1 ? "" : "s"} removed from Field Labels: ${shortList(remove)}. Their answers stay.` : "",
    restore.length ? `${restore.length} row${restore.length === 1 ? "" : "s"} back from Field Labels: ${shortList(restore)}.` : "",
  ].filter(Boolean).join(" ");
  if (!rows.length) {
    if (said || carried) s.flash = { text: `${said}${carried}`.trim(), tone: "green" };
    return paint();
  }
  if (s.hasDraft && unfinishedDraft()) s.queue = [...s.queue, ...rows];
  else {
    s.draft = rows[0]!;
    s.queue = rows.slice(1);
    s.after = null;
  }
  s.check = null;
  s.run = true;
  s.pick = NEW;
  s.hasDraft = true;
  s.flash = { text: `${said}${carried ? `${carried} ` : ""}${rows.length} row${rows.length === 1 ? "" : "s"} came in from Field Labels. Fill the yellow cells on each; it saves when complete. The rest wait their turn, kept on this iPad.`, tone: "amber" };
  rememberDraft();
  paint();
}

/** "00-ER2-MCC-01-A3 and A4", "00-ER2-MCC-01-A3, A4 and 3 more": the first ID whole, then what differs. */
function shortList(ids: readonly string[]): string {
  const first = ids[0] ?? "";
  const tail = (id: string) => {
    let i = 0;
    while (i < id.length && id[i] === first[i]) i += 1;
    const cut = id.lastIndexOf("-", i - 1);
    return cut > 0 ? id.slice(cut + 1) : id;
  };
  const rest = ids.slice(1, 3).map(tail);
  const more = ids.length - 3;
  const parts = [first, ...rest];
  if (more > 0) return `${parts.join(", ")} and ${more} more`;
  return parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : first;
}

/** Copy Down To: the run lands as the new row plus rows waiting their turn, each with the four answers blank. */
async function addRange(): Promise<void> {
  const s = st!;
  if (s.sheet?.kind !== "downTo") return;
  const classes = classesOf(s.pack);
  const src = copySource(classes);
  if (!src) return;
  const ids = existingIds();
  const list = copyDownSeries(src.form, s.sheet.to).filter((f) => !ids.has(assetIdOf(s.pack.siteNumber, f, classes)));
  if (!list.length) return;
  s.sheet = null;
  if (src.draft && !missingFields(src.form).length) {
    const id = await commitDraft();
    if (id) {
      s.draft = list[0]!;
      s.queue = list.slice(1);
      s.after = id;
    }
  } else if (src.draft) {
    // The new row still needs its answers: it stays first, the run lines up behind it.
    s.queue = list;
  } else {
    s.draft = list[0]!;
    s.queue = list.slice(1);
    s.after = src.id;
  }
  s.check = null;
  s.run = true;
  s.pick = NEW;
  s.hasDraft = true;
  rememberDraft();
  const first = assetIdOf(s.pack.siteNumber, list[0]!, classes).replace(/^\d{2}-/, "");
  const last = assetIdOf(s.pack.siteNumber, list[list.length - 1]!, classes).replace(/^\d{2}-/, "");
  s.flash = { text: `${list.length} rows lined up, ${first} to ${last}. Answer the four on each. Each saves as you finish it.`, tone: "amber" };
  paint();
}

// ---- QR labels ----

/**
 * Update PDF: the whole site's QR labels, room by room with a room page first, at the roll's start offset.
 * Goes to 02 QR Labels as 10_QR_Labels.pdf, replacing the one there. The new ones now count as in it.
 */
async function updateMaster(sheet: PrintSheet, roll: PcRoll): Promise<void> {
  const s = st!;
  if (sheet.pc.busy) return;
  sheet.pc.busy = `update:${roll.key}`;
  sheet.pc.error = "";
  paint();
  try {
    const parts = await Promise.all(sectionsOf(roll).map(async (sec) => ({ room: sec.room, line: `${sec.items.length} label${sec.items.length === 1 ? "" : "s"}`, bytes: await qrLabelsPdf(sec.items.map((i) => i.key)) })));
    const bytes = await assembleMaster(`SafeTag QR Labels, site ${s.pack.siteNumber}`, parts);
    sheet.pc.masterFiles.set(roll.key, new File([bytes as Uint8Array<ArrayBuffer>], roll.driveName, { type: "application/pdf" }));
    await sendMaster(sheet.pc, { company: host!.badge.company, site: s.pack.siteNumber, kind: "qr", driveName: roll.driveName, bytes, badgeId: host!.badge.badgeId, byName: host!.badge.holderName }, schedulePaint);
    const fresh = roll.items.filter((i) => i.state === "new").map((i) => i.key);
    const t = stamp();
    const all = docs(s.pack);
    bulk = true;
    try {
      // Rows on the sheet take the stamp. Walk IDs not pushed yet are marked in the walk, so both cards agree.
      for (const id of fresh) if (all[id]) await saveAsset(id, { qrLabelPrintedAt: t.at, updatedAt: t.at, updatedBy: t.badgeId, updatedByName: t.holderName }, "Label printed");
    } finally {
      bulk = false;
    }
    stampQrInWalk(host!.badge.company, s.pack.siteNumber, fresh, t.at);
  } catch (e) {
    sheet.pc.error = `Could not update the PDF: ${e instanceof Error ? e.message : String(e)}`;
  }
  sheet.pc.busy = "";
  paint();
}

/** Make PDF: just the picked labels, at this iPad's offset. For the printer. Never to Drive. */
async function makePdf(sheet: PrintSheet, roll: PcRoll): Promise<void> {
  const s = st!;
  const ids = pickedOf(roll, sheet.pc).map((i) => i.key);
  if (!ids.length || sheet.pc.busy) return;
  sheet.pc.busy = `make:${roll.key}`;
  sheet.pc.error = "";
  paint();
  try {
    const bytes = await qrLabelsPdf(ids, readOffset("qrRoll"));
    const name = `${s.pack.siteNumber}_QR_Labels_Print_${ids.length === 1 ? ids[0]!.replace(/^\d{2}-/, "") : `${ids.length}_labels`}.pdf`;
    sheet.pc.small.set(roll.key, { file: new File([bytes as Uint8Array<ArrayBuffer>], name, { type: "application/pdf" }), count: ids.length });
  } catch (e) {
    sheet.pc.error = `Could not make the PDF: ${e instanceof Error ? e.message : String(e)}`;
  }
  sheet.pc.busy = "";
  paint();
}

// ---- Refused saves ----

async function renumber(assetId: string): Promise<void> {
  const s = st!;
  const r = local.find((x) => x.assetId === assetId && x.state === "refused");
  if (!r) return;
  await dropLocal(r);
  s.draft = formOfRecord(assetId, r.data, classesOf(s.pack));
  s.hasDraft = true;
  s.after = null;
  rememberDraft();
  s.pick = NEW;
  s.scrollTo = true;
  s.flash = { text: `Change the number or END location in the blank row. ${assetId} stays with the other iPad.`, tone: "amber" };
  paint();
}

async function drop(assetId: string): Promise<void> {
  const r = local.find((x) => x.assetId === assetId && x.state === "refused");
  if (!r) return;
  await dropLocal(r);
  st!.flash = { text: `Dropped this iPad's copy of ${assetId}.`, tone: "amber" };
  paint();
}

/** Takes a refused save off this iPad. The pack goes back to what the cloud holds. */
async function dropLocal(r: RecordEntry): Promise<void> {
  const s = st!;
  await deviceList.deleteRecord(r.path);
  await deviceList.addLog(`Dropped ${r.assetId} · ${r.card}`);
  await saveChange(r.assetId, "Dropped", {}, isCreate(r.data) ? "The cloud refused this iPad's new asset. Its copy was dropped." : "The cloud refused this iPad's change. It was dropped.");
  let cloud: Record<string, unknown> | null = null;
  try {
    cloud = isOnline() && host ? await cloudAsset(host.badge.company, s.pack.siteNumber, r.assetId) : null;
  } catch {
    cloud = null;
  }
  const all = docs(s.pack);
  if (cloud) {
    all[r.assetId] = cloud;
    applyToPack(s.pack, r.assetId, cloud);
  } else if (isCreate(r.data)) {
    delete all[r.assetId];
    const i = s.pack.assets.findIndex((a) => a.assetId === r.assetId);
    if (i >= 0) s.pack.assets.splice(i, 1);
  }
  takenCheck.delete(r.assetId);
  host?.assetsChanged();
  await refreshLocal();
}

/** Two taps on a row number this close together are a double-tap. */
export const DOUBLE_TAP_MS = 300;
/** Holding a row number this long opens its menu. */
export const HOLD_MS = 550;
let gutterTap: { key: string; at: number; timer: number } | null = null;
let holdTimer: number | null = null;
let holdFrom: { x: number; y: number } | null = null;
let heldOpen = false;

/** One tap or two on the same thing: the second tap within DOUBLE_TAP_MS makes it a double-tap. */
let tapPend: { key: string; timer: number } | null = null;
function tapOrDouble(key: string, single: () => void, double: () => void): void {
  if (tapPend && tapPend.key === key) {
    clearTimeout(tapPend.timer);
    tapPend = null;
    double();
    return;
  }
  if (tapPend) clearTimeout(tapPend.timer);
  tapPend = { key, timer: window.setTimeout(() => {
    tapPend = null;
    single();
  }, DOUBLE_TAP_MS) };
}
/** The last tap on an empty number cell of the new row, for its double-tap. */
let lastInputTap: { key: string; at: number } | null = null;
/** The new row's typed cell and its value when you entered it. */
let focusValue: { cell: keyof IdForm; value: string } | null = null;

/** Picking another row. Leaving the new row saves it when it is complete. */
async function moveTo(key: string): Promise<void> {
  const s = st!;
  if (s.pick === key) return;
  s.flash = null;
  if (s.pick === NEW && key !== NEW && s.hasDraft) await leaveDraft();
  s.pick = key;
  paint();
}

// ---- Events ----

function wire(): void {
  if (wired || !host) return;
  wired = true;
  const root = host.root;
  root.addEventListener("click", (ev) => {
    if (!onThisPage() || !st) return;
    const target = ev.target as HTMLElement;
    const dv = target.closest<HTMLElement>("[data-dv]");
    if (dv) {
      if (dv.dataset.dv === "build") void askForFolder();
      return;
    }
    const pc = target.closest<HTMLElement>("[data-pc]");
    if (pc && st.sheet?.kind === "print") {
      if (!(pc as HTMLButtonElement).disabled) void qrCardClick(pc);
      return;
    }
    const el = target.closest<HTMLElement>("[data-ai]");
    if (!el) {
      if (target.matches("[data-ai-sheet], [data-pc-sheet]") && !(st.sheet?.kind === "print" && st.sheet.pc.busy)) {
        if (st.sheet?.kind === "print") stopMasterWatches();
        st.sheet = null;
        st.menu = null;
        st.list = null;
        st.def = null;
        paint();
      }
      return;
    }
    if ((el as HTMLButtonElement).disabled) return;
    const s = st;
    const a = el.dataset.ai;
    const key = el.dataset.key ?? "";
    // The line under the bar clears on the next tap (rev 6 section 4).
    if (a !== "close") s.flash = null;
    switch (a) {
      case "pick":
        if (key.startsWith("queue-")) return;
        if (s.pick !== key) void moveTo(key);
        break;
      case "row": {
        // The row number (rev 6): one tap picks the row, a double-tap adds a blank row under it,
        // press and hold opens its menu. A press that opened the menu is not also a tap.
        if (heldOpen) {
          heldOpen = false;
          return;
        }
        const now = Date.now();
        if (gutterTap && gutterTap.key === key && now - gutterTap.at < DOUBLE_TAP_MS) {
          clearTimeout(gutterTap.timer);
          gutterTap = null;
          void moveTo(key).then(() => addRowUnder(key));
        } else {
          if (gutterTap) clearTimeout(gutterTap.timer);
          gutterTap = { key, at: now, timer: window.setTimeout(() => {
            gutterTap = null;
            void moveTo(key);
          }, DOUBLE_TAP_MS) };
        }
        break;
      }
      case "yn": {
        const cell = el.dataset.cell as keyof IdForm;
        const v = el.dataset.v ?? "";
        if (key === NEW) {
          s.draft = cell === "active" ? withExposure(s.draft, v) : { ...s.draft, [cell]: v };
          s.pick = NEW;
          rememberDraft();
          void settleDraft();
        } else void editRow(key, cell, v);
        break;
      }
      case "goto": {
        const id = el.dataset.id ?? "";
        if (!docs(s.pack)[id]) return;
        s.find = "";
        void moveTo(id).then(() => {
          if (!st) return;
          st.scrollTo = true;
          paint();
        });
        break;
      }
      case "cell": {
        // A list cell: one tap opens its list. An empty cell on the new row waits a moment: a second tap fills it from above.
        const cell = el.dataset.cell as keyof IdForm;
        if (s.pick !== key) void moveTo(key);
        const form = key === NEW ? s.draft : pickedForm(classesOf(s.pack))?.form;
        if (!form) return;
        if (key !== NEW || form[cell] || !(FILL_FIELDS as readonly string[]).includes(cell)) {
          openList(key, cell);
          return;
        }
        tapOrDouble(`${key}:${cell}`, () => openList(key, cell), () => {
          if (fillCell(cell as FillField)) void settleDraft();
          else paint();
        });
        break;
      }
      case "fill-row":
        if (s.pick !== NEW) void moveTo(NEW);
        tapOrDouble("fill-row", () => undefined, fillRow);
        break;
      case "list-pick": {
        const l = s.list;
        s.list = null;
        if (!l) return paint();
        setCell(l.key, l.cell, el.dataset.v ?? "");
        break;
      }
      case "def": {
        // A column header: its definition card under it. Tap anywhere to close.
        const field = el.dataset.def as DefinedField;
        const b = el.getBoundingClientRect();
        s.def = { field, left: Math.max(8, Math.min(b.left, window.innerWidth - 320)), top: b.bottom + 6 };
        paint();
        break;
      }
      case "aa": {
        const i = (ZOOMS as readonly number[]).indexOf(s.zoom);
        const next = ZOOMS[(i + 1) % ZOOMS.length]!;
        zoomRatio = next / s.zoom;
        s.zoom = next;
        try {
          window.localStorage.setItem(ZOOM_KEY, String(next));
        } catch {
          // Not remembered. It still sizes.
        }
        paint();
        break;
      }
      case "down-to":
        s.sheet = { kind: "downTo", to: "" };
        paint();
        root.querySelector<HTMLInputElement>("#ai-range-to")?.focus();
        break;
      case "range-go":
        void addRange();
        break;
      case "bringin":
        s.sheet = { kind: "bringIn", room: null };
        paint();
        break;
      case "bringin-go":
        void bringInGo();
        break;
      case "calcs":
        s.calcs = !s.calcs;
        try {
          window.localStorage.setItem(CALCS_KEY, s.calcs ? "on" : "off");
        } catch {
          // Not remembered. It still shows.
        }
        paint();
        break;
      case "remove-ask":
        s.menu = null;
        if (fromWalk(docs(s.pack)[key])) break;
        s.sheet = { kind: "remove", id: key };
        paint();
        break;
      case "remove-go":
        s.sheet = null;
        void setRemoved(key, true);
        break;
      case "restore":
        s.menu = null;
        if (fromWalk(docs(s.pack)[key])) break;
        void setRemoved(key, false);
        break;
      case "clear-new": {
        s.menu = null;
        // The rows taken out never reach the sheet: Field Labels drops their green ON SHEET (item 8).
        const cls = classesOf(s.pack);
        const here = existingIds();
        const cleared = (s.hasDraft ? [s.draft, ...s.queue] : []).map((f) => assetIdOf(s.pack.siteNumber, f, cls)).filter((id) => id && !here.has(id));
        unpushInWalk(host!.badge.company, s.pack.siteNumber, cleared);
        s.draft = { ...BLANK_FORM };
        s.hasDraft = false;
        s.after = null;
        s.check = null;
        s.queue = [];
        s.run = false;
        rememberDraft();
        paint();
        break;
      }
      case "show-removed":
        s.menu = null;
        s.showRemoved = !s.showRemoved;
        paint();
        break;
      case "refresh":
        host?.refreshPack();
        break;
      case "close":
        if (s.sheet?.kind === "print" && s.sheet.pc.busy) return;
        s.sheet = null;
        s.menu = null;
        s.list = null;
        paint();
        break;
      case "print": {
        const sh: PrintSheet = { kind: "print", pc: newPcState() };
        s.sheet = sh;
        paint();
        void loadMasterState(sh.pc, host!.badge.company, s.pack.siteNumber, schedulePaint);
        break;
      }
      case "renumber":
        void renumber(el.dataset.id ?? "");
        break;
      case "drop":
        void drop(el.dataset.id ?? "");
        break;
    }
  });
  // Press and hold a row number: its menu (Remove, Restore, Clear), the iPad way to more options.
  const stopHold = () => {
    if (holdTimer !== null) clearTimeout(holdTimer);
    holdTimer = null;
    holdFrom = null;
  };
  root.addEventListener("pointerdown", (ev) => {
    if (!onThisPage() || !st) return;
    const g = (ev.target as HTMLElement).closest<HTMLElement>('[data-ai="row"]');
    if (!g) return;
    const key = g.dataset.key ?? "";
    stopHold();
    // A new press starts clean: the menu a hold opened earlier never swallows this tap.
    heldOpen = false;
    holdFrom = { x: ev.clientX, y: ev.clientY };
    holdTimer = window.setTimeout(() => {
      holdTimer = null;
      if (!st) return;
      heldOpen = true;
      if (gutterTap) clearTimeout(gutterTap.timer);
      gutterTap = null;
      void moveTo(key).then(() => {
        if (!st) return;
        st.menu = key;
        paint();
      });
    }, HOLD_MS);
  });
  root.addEventListener("pointermove", (ev) => {
    if (holdFrom && Math.hypot(ev.clientX - holdFrom.x, ev.clientY - holdFrom.y) > 10) stopHold();
  });
  root.addEventListener("pointerup", stopHold);
  root.addEventListener("pointercancel", stopHold);
  root.addEventListener("contextmenu", (ev) => {
    if (onThisPage() && (ev.target as HTMLElement).closest('[data-ai="row"]')) ev.preventDefault();
  });
  // A number cell on the new row (Loc #, Equip #, END): the first tap opens the keyboard. A quick second tap on
  // an empty one fills it from above and closes the keyboard (rev 6 section 1).
  root.addEventListener("click", (ev) => {
    if (!onThisPage() || !st) return;
    const t = ev.target as HTMLInputElement;
    const cell = t.dataset?.aiCell as FillField | undefined;
    if (t.tagName !== "INPUT" || !cell || t.dataset.key !== NEW) return;
    const now = Date.now();
    if (!t.value && lastInputTap?.key === cell && now - lastInputTap.at < DOUBLE_TAP_MS + 50) {
      lastInputTap = null;
      if (fillCell(cell)) {
        t.value = st.draft[cell];
        t.blur();
      }
      paint();
      return;
    }
    lastInputTap = { key: cell, at: now };
  });
  root.addEventListener("change", (ev) => {
    if (!onThisPage() || !st) return;
    const t = ev.target as HTMLInputElement | HTMLSelectElement;
    const cell = t.dataset?.aiCell as keyof IdForm | undefined;
    const key = t.dataset?.key;
    if (cell && key && t.tagName === "SELECT") {
      setCell(key, cell, t.value);
      return;
    }
  });
  // A typed cell in the blank row settles when you leave it (Enter or a tap elsewhere), like a workbook cell.
  // The repaint while typing swaps the input, so its change event cannot be trusted; leaving the cell can.
  root.addEventListener("focusout", (ev) => {
    if (!onThisPage() || !st) return;
    const t = ev.target as HTMLInputElement;
    // Sites Equipment Tag settles when you leave it, like the other typed cells. Not part of the ID.
    if (t.dataset?.aiTag !== undefined) {
      if (painting || !t.isConnected) return;
      const rowKey = t.dataset.key ?? "";
      if (rowKey === NEW) {
        st.draft = { ...st.draft, tag: tagText(t.value) };
        rememberDraft();
        void settleDraft(true);
        return;
      }
      tagTyping = null;
      const doc = docs(st.pack)[rowKey];
      if (doc && tagText(t.value) !== tagText(String(doc.siteEquipmentTag ?? ""))) void editRow(rowKey, "tag", t.value);
      return;
    }
    const cell = t.dataset?.aiCell as keyof IdForm | undefined;
    if (painting || t.tagName !== "INPUT" || !cell || !t.isConnected) return;
    const rowKey = t.dataset.key ?? "";
    if (rowKey !== NEW) {
      // A saved row's ID cell, open because nothing carries the ID yet.
      const doc = docs(st.pack)[rowKey];
      if (doc && (ID_FIELDS as readonly string[]).includes(cell) && cleanPart(t.value) !== cleanPart(formOfRecord(rowKey, doc, classesOf(st.pack))[cell])) void renameRow(rowKey, cell, cleanPart(t.value));
      return;
    }
    // Compare with the value when the cell was entered: typing already updated the row as you went.
    const before = focusValue && focusValue.cell === cell ? focusValue.value : st.draft[cell];
    focusValue = null;
    st.draft = { ...st.draft, [cell]: cleanPart(t.value) };
    const changed = st.draft[cell] !== before;
    if (changed) {
      if (st.check === cell) st.check = null;
      if (cell === "locNum") restartNumber(cell);
    }
    rememberDraft();
    // Nothing changed: no repaint. A repaint here swaps the sheet under the finger and the tap that moved you is lost.
    void settleDraft(!changed);
  });
  root.addEventListener("focusin", (ev) => {
    if (!onThisPage() || !st || painting) return;
    const t = ev.target as HTMLInputElement;
    const cell = t.dataset?.aiCell as keyof IdForm | undefined;
    if (t.tagName === "INPUT" && cell && t.dataset.key === NEW) focusValue = { cell, value: st.draft[cell] };
  });
  root.addEventListener("input", (ev) => {
    if (!onThisPage() || !st) return;
    const t = ev.target as HTMLInputElement;
    if (t.tagName !== "INPUT") return;
    if (st.sheet?.kind === "print" && cardFind(t, st.sheet.pc, paint, host!.root)) return;
    if (t.dataset?.aiTag !== undefined) {
      // Kept as typed. No repaint: the tag is not part of the ID. A repaint meanwhile keeps what was typed.
      const value = t.value.slice(0, TAG_MAX);
      if (t.dataset.key === NEW) {
        st.draft = { ...st.draft, tag: value };
        rememberDraft();
      } else tagTyping = { key: t.dataset.key ?? "", value };
      return;
    }
    const cell = t.dataset?.aiCell as keyof IdForm | undefined;
    if (cell && t.dataset.key === NEW) {
      // The Asset ID builds as you type, like the workbook's formula column. It saves on Enter or leaving the cell.
      st.draft = { ...st.draft, [cell]: cleanPart(t.value) };
      if (st.check === cell) st.check = null;
      rememberDraft();
      paint();
    } else if (t.dataset?.aiFind !== undefined) {
      st.find = t.value;
      paint();
    } else if (t.dataset?.aiRange !== undefined && st.sheet?.kind === "downTo") {
      st.sheet.to = t.value;
      paint();
    }
  });
  root.addEventListener("keydown", (ev) => {
    if (!onThisPage() || !st || ev.key !== "Enter") return;
    const t = ev.target as HTMLElement;
    if (t.dataset?.aiRange !== undefined) {
      ev.preventDefault();
      void addRange();
    } else if (t.dataset?.aiCell || t.dataset?.aiTag !== undefined) {
      ev.preventDefault();
      (t as HTMLInputElement).blur();
    }
  });
  window.addEventListener("resize", () => {
    const wrap = host?.root.querySelector<HTMLElement>("#ac-wrap");
    if (wrap && onThisPage()) fitHeight(wrap);
  });
  onOutboxChange(() => void refreshLocal());
}
